/**
 * The decorative-SVG transform (DS-2/DS-3) — the ONE place the pipeline lives.
 *
 * WHY THIS IS ITS OWN MODULE, and why the ids are parameters.
 *
 * The library shows, sorts and names by a property the insert used to destroy:
 * `makeSvgPaintThemeable` walked every descendant and rewrote every `fill` and
 * `stroke` to `currentColor`, unconditionally, on insert AND on replace. So
 * "blue splash", "blue splash 2" (orange) and "blue splash 3" (yellow) were one
 * shape family in three colours that all became the same colour.
 *
 * DS-3 fixes that by PREVIEWING the transformed result. A preview is only worth
 * having if it cannot drift from the action, so both the preview and
 * `insertDecorativeSvg` call this one function — never a model of it.
 *
 * `idPrefix` and `eid` are INJECTED rather than generated here. `insertDecorativeSvg`
 * built them from `Date.now()` + `Math.random()`, which makes "the preview equals
 * the insert" untestable: two runs of the same input differ in the id prefix alone
 * and a byte-comparison is impossible. The generators stay at the call site; this
 * function is a pure function of its arguments, and that is what makes DS-3's
 * load-bearing equality assertion reachable at all rather than quietly weakened
 * into a substring check.
 *
 * "ORIGINAL" MEANS THE SANITIZED SOURCE PAINT — NOT BYTE-IDENTICAL SOURCE MARKUP.
 * The pipeline still runs `sanitizeSvg`, still strips `width`/`height`, still
 * prefixes ids, still stamps `aria-hidden` / `focusable` / the eid. The only thing
 * `colorToken: 'original'` changes is that `fill` and `stroke` keep the values the
 * file shipped with. Said in these words on purpose: "original" is otherwise read
 * as "untouched", which it is not and must never be.
 *
 * CSS `mask-image` was rejected for the preview. It is exact for flat art and a
 * LIE about gradient art, which it reports as a solid silhouette — and 370 of the
 * corpus's 1,150 entries (32%) carry `defs`, whose `url(...)` paint
 * `shouldRewriteSvgPaint` deliberately skips.
 */

import { parseSvg, sanitizeSvg, prefixSvgIds, serializeSvg } from './svg-sanitizer.js';

/**
 * The colour tokens a decorative SVG may carry.
 *
 * `'original'` is FIRST and it is the default. Plenty of these shapes are
 * coloured the way they are for the stitch they came from, and that is fine.
 */
export const DECORATIVE_COLOR_TOKENS = Object.freeze([
  'original',
  '--primary',
  '--secondary',
  '--headerColor',
  '--bodyTextColor',
  'currentColor'
]);

const COLOR_TOKEN_SET = new Set(DECORATIVE_COLOR_TOKENS);

/** Where a flattened paint value is parked so it can be put back. */
export const PAINT_MEMO_ATTRS = Object.freeze({
  fill: 'data-pb-paint-fill',
  stroke: 'data-pb-paint-stroke'
});

/**
 * The sentinel recorded for an element that had NO such attribute at all.
 *
 * It has to be impossible as a real paint value, and the first draft achieved
 * that with a literal NUL byte. `ui-consistency-sweep`'s no-NUL-byte invariant
 * caught it — a rule that had presumably never fired before, doing exactly its
 * job. `@` fails `shouldRewriteSvgPaint`'s `^(#|rgb|hsl|[a-z]+$)` just as well
 * and is legible in a diff.
 */
const PAINT_ABSENT = '@pb-absent';

export function isDecorativeColorToken(value) {
  return COLOR_TOKEN_SET.has(value);
}

/**
 * Is this a paint value we may rewrite?
 *
 * `none` / `transparent` / `currentColor` / `inherit` are already
 * theme-neutral. `url(...)` is a gradient or pattern reference and rewriting it
 * would destroy the artwork — which is exactly why a mask-image preview would
 * be a lie about 32% of the corpus.
 */
export function shouldRewriteSvgPaint(value) {
  const raw = String(value || '').trim();
  if (!raw) return false;
  if (/^(none|transparent|currentColor|inherit)$/i.test(raw)) return false;
  if (/^url\(/i.test(raw)) return false;
  return /^(#|rgb|hsl|[a-z]+$)/i.test(raw);
}

/**
 * Flatten every rewritable paint to `currentColor`, RECORDING what was there.
 *
 * The recording is the whole point. Without it "Original" is a one-way door:
 * the source paint leaves the stored markup the moment a token is applied, and
 * `setDecorativeSvgColor` re-flattens on every change — so a decorative
 * inserted before this existed can never be returned to its own colours,
 * because they were destroyed and never written down.
 *
 * A shape with no `fill` ANYWHERE records the ABSENT sentinel on its root — see
 * `themeAbsentRootFill` below, and note that absent `stroke` is deliberately
 * never filled in. The sentinel is what lets restore tell "it was `#123456`"
 * from "it had none" from "this was never flattened", so putting it back
 * removes the attribute rather than writing a colour the file never had.
 *
 * The memo attributes are `data-pb-*` DELIBERATELY: they are builder-only
 * bookkeeping and `stripAllBuilderAttrs` removing them on export is correct.
 */
export function makeSvgPaintThemeable(svg) {
  const targets = [svg, ...Array.from(svg.querySelectorAll('*'))];
  for (const el of targets) {
    for (const attr of ['fill', 'stroke']) {
      const memo = PAINT_MEMO_ATTRS[attr];
      const current = el.getAttribute(attr);
      if (!shouldRewriteSvgPaint(current)) continue;
      // Only the FIRST flatten records: re-flattening an already-themeable
      // element would overwrite the source value with `currentColor`.
      if (!el.hasAttribute(memo)) el.setAttribute(memo, current);
      el.setAttribute(attr, 'currentColor');
    }
  }
  themeAbsentRootFill(svg);
  return svg;
}

/**
 * Make a shape with NO `fill` anywhere recolour, by writing one on the root.
 *
 * THE ASYMMETRY IS THE WHOLE RULE, and getting it wrong in either direction is
 * a visible defect:
 *
 *   · `fill`'s initial value is **black**. A `<path>` with no `fill` is not
 *     unpainted — it renders black — so under a token it MUST recolour, and
 *     before this it silently did not. That is the gap this function closes.
 *   · `stroke`'s initial value is **`none`**. Writing `stroke="currentColor"`
 *     would DRAW STROKES THAT WERE NEVER IN THE ARTWORK, outlining every shape
 *     in the file. Absent stroke is therefore never filled in — not here, not
 *     anywhere.
 *
 * ON THE ROOT, NOT ON LEAVES. `fill` is an inherited presentation attribute, so
 * one root write reaches every descendant that sets none, while writing it onto
 * leaves would OVERRIDE a `<g>`'s fill and break grouped artwork. It is skipped
 * entirely when the root already has a fill (the loop above handled that) or
 * when some descendant carries recorded paint of its own — in that second case
 * the shape's colour is already themeable and a root fill would only repaint
 * the gaps the artist left deliberately.
 *
 * The ABSENT sentinel is what makes this reversible: restore removes the
 * attribute rather than writing `black`, so the shape returns to the inherited
 * default it actually had.
 */
function themeAbsentRootFill(svg) {
  if (svg.hasAttribute('fill') || svg.hasAttribute(PAINT_MEMO_ATTRS.fill)) return;
  if (!inheritsDefaultFill(svg)) return;
  svg.setAttribute(PAINT_MEMO_ATTRS.fill, PAINT_ABSENT);
  svg.setAttribute('fill', 'currentColor');
}

/**
 * The elements that actually render fill. `<defs>`, `<title>`, `<stop>`,
 * `<linearGradient>` and friends do not, and `<g>` / `<use>` are containers
 * whose own paint only matters through what they contain — so the probe is on
 * LEAVES, and a file made only of definitions has no fill to give.
 */
const PAINTABLE_LEAF_SELECTOR = 'path, circle, ellipse, rect, line, polyline, polygon, text, tspan';

/**
 * Does anything here actually fall back to the initial black?
 *
 * True only when some paintable LEAF has no `fill` of its own and no ancestor
 * inside the SVG supplies one. Without that check a root fill would be written
 * onto a file whose every leaf already paints itself — harmless, because the
 * leaf's own `fill` wins, but a real attribute that reaches nothing, and the
 * gradient fixture is exactly that shape.
 */
function inheritsDefaultFill(svg) {
  for (const el of Array.from(svg.querySelectorAll(PAINTABLE_LEAF_SELECTOR))) {
    if (el.hasAttribute('fill') || el.hasAttribute(PAINT_MEMO_ATTRS.fill)) continue;
    // `closest` stops at the root, which we already know carries no fill.
    const painted = el.closest?.(`[fill], [${PAINT_MEMO_ATTRS.fill}]`);
    if (!painted || painted === svg) return true;
  }
  return false;
}

/**
 * Put the recorded paint back and delete the memo.
 *
 * @returns {boolean} whether ANY memo was found. False means this markup
 *   predates the recording — the honest answer is "cannot", never a silent
 *   no-op that leaves the shape flattened while the control says Original.
 */
export function restoreSvgPaint(svg) {
  const targets = [svg, ...Array.from(svg.querySelectorAll('*'))];
  let found = false;
  for (const el of targets) {
    for (const attr of ['fill', 'stroke']) {
      const memo = PAINT_MEMO_ATTRS[attr];
      if (!el.hasAttribute(memo)) continue;
      found = true;
      const value = el.getAttribute(memo);
      if (value === PAINT_ABSENT) el.removeAttribute(attr);
      else el.setAttribute(attr, value);
      el.removeAttribute(memo);
    }
  }
  return found;
}

/**
 * Does this markup carry recorded paint anywhere?
 *
 * The question a caller has to be able to ask BEFORE offering "Original" on a
 * decorative that may predate the recording.
 */
export function hasRecordedPaint(svg) {
  if (!svg) return false;
  if (svg.hasAttribute(PAINT_MEMO_ATTRS.fill) || svg.hasAttribute(PAINT_MEMO_ATTRS.stroke)) {
    return true;
  }
  return !!svg.querySelector(`[${PAINT_MEMO_ATTRS.fill}], [${PAINT_MEMO_ATTRS.stroke}]`);
}

/**
 * Is there paint here that looks FLATTENED but was never recorded?
 *
 * This is the distinction the first draft of DS-2 collapsed, and the round-trip
 * test caught it. "No memo" has two causes and they are not the same:
 *
 *   · the shape had NOTHING rewritable to begin with — a `<circle>` with no
 *     `fill` and no `stroke`. Nothing was destroyed, so "Original" is
 *     trivially satisfiable and refusing it would be a lie in the other
 *     direction.
 *   · the shape's paint was flattened to `currentColor` by the pre-DS-2 code,
 *     which wrote nothing down. That original is gone.
 *
 * The tell is a literal `currentColor` in a paint attribute with no memo
 * beside it. An author who wrote `currentColor` themselves is indistinguishable
 * from a flatten — deliberately unresolved, and refusing is the safe half:
 * their "original" IS `currentColor`, so the only cost is an option they did
 * not need, where the other error is a control that silently does nothing.
 */
export function hasUnrecordedFlatten(svg) {
  if (!svg) return false;
  const targets = [svg, ...Array.from(svg.querySelectorAll('*'))];
  for (const el of targets) {
    for (const attr of ['fill', 'stroke']) {
      if (!/^currentColor$/i.test(String(el.getAttribute(attr) || ''))) continue;
      if (!el.hasAttribute(PAINT_MEMO_ATTRS[attr])) return true;
    }
  }
  return false;
}

/**
 * Apply a colour token to a decorative SVG element, in place.
 *
 * `'original'` removes the inline `color`, restores recorded paint and stamps
 * the token. Every other token flattens (recording as it goes) and pins
 * `color: var(<token>, currentColor)`.
 *
 * @returns {{ok: boolean, reason?: 'no-recorded-paint'}}
 */
export function applyDecorativeColor(svg, colorToken = 'original') {
  const token = isDecorativeColorToken(colorToken) ? colorToken : 'original';

  if (token === 'original') {
    // Refuse ONLY when paint was flattened and not written down — never merely
    // because there is no memo, which is also true of a shape that had nothing
    // rewritable in the first place. See `hasUnrecordedFlatten`.
    if (!hasRecordedPaint(svg) && hasUnrecordedFlatten(svg)) {
      return { ok: false, reason: 'no-recorded-paint' };
    }
    restoreSvgPaint(svg);
    setInlineColor(svg, null);
    svg.setAttribute('data-pb-decorative-color', 'original');
    return { ok: true };
  }

  makeSvgPaintThemeable(svg);
  svg.setAttribute('data-pb-decorative-color', token);
  setInlineColor(svg, token === 'currentColor' ? null : `var(${token}, currentColor)`);
  return { ok: true };
}

/**
 * Write (or clear) the inline `color`, tolerating a root with no `.style`.
 *
 * A parsed SVG only has a CSSStyleDeclaration if its root is in the SVG
 * NAMESPACE — which requires an `xmlns` on the source. Every real `.svg` file
 * has one (a standalone SVG document is invalid without it), but markup pulled
 * from somewhere else may not, and `parseSvg`'s check is on the TAG NAME, so
 * such a root parses fine and then throws here.
 *
 * That throw is worse than it looks: `createThumbnailPool` catches and swallows
 * resolver failures, so a namespace-less shape would silently keep its `<img>`
 * with nothing said. Attribute-first, `.style` when it exists — the paint
 * transform is the half that matters, and it has already run.
 */
function setInlineColor(svg, value) {
  if (svg.style && typeof svg.style.setProperty === 'function') {
    if (value === null) svg.style.removeProperty('color');
    else svg.style.setProperty('color', value);
    return;
  }
  const style = String(svg.getAttribute('style') || '')
    .split(';')
    .map((part) => part.trim())
    .filter((part) => part && !/^color\s*:/i.test(part));
  if (value !== null) style.push(`color: ${value}`);
  if (style.length) svg.setAttribute('style', `${style.join('; ')};`);
  else svg.removeAttribute('style');
}

/**
 * The whole insert-time transform, as a pure function of its arguments.
 *
 * @param {string} markup — raw SVG source
 * @param {{colorToken?: string, idPrefix?: string, eid?: string, name?: string,
 *          filename?: string, decorative?: boolean}} opts
 *   `idPrefix` and `eid` are the caller's (see the file header). `decorative:
 *   false` skips the builder markers, which is what the LIBRARY PREVIEW wants:
 *   the same paint transform, none of the canvas bookkeeping.
 * @returns {SVGElement|null} the transformed element, or null if unparseable.
 */
export function prepareDecorativeSvg(markup, opts = {}) {
  const svg = parseSvg(markup);
  if (!svg) return null;

  const {
    colorToken = 'original',
    idPrefix = '',
    eid = '',
    name = '',
    filename = '',
    decorative = true
  } = opts;

  sanitizeSvg(svg);
  if (idPrefix) prefixSvgIds(svg, idPrefix);

  svg.removeAttribute('width');
  svg.removeAttribute('height');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  if (decorative) {
    svg.setAttribute('data-pb-decorative', 'true');
    if (name) svg.setAttribute('data-pb-decorative-name', name);
    // The source file. Nothing stored it before — only the display name — and
    // it is what would make a future "re-fetch the original" possible at all.
    if (filename) svg.setAttribute('data-pb-decorative-src', filename);
    if (eid) svg.setAttribute('data-pb-eid', eid);
  }

  applyDecorativeColor(svg, colorToken);
  if (!decorative) {
    // `applyDecorativeColor` stamps the token because a canvas element needs
    // it — the panel reads it back to paint the select. A PREVIEW is a detached
    // picture that nothing reads it off, so it leaves with the rest of the
    // bookkeeping. The inline `color` stays: that one is not bookkeeping, it is
    // how a `currentColor` shape shows the colour it will actually be.
    svg.removeAttribute('data-pb-decorative-color');
  }
  return svg;
}

/** `prepareDecorativeSvg` + serialize, for callers that want the string. */
export function renderDecorativeSvg(markup, opts = {}) {
  const svg = prepareDecorativeSvg(markup, opts);
  return svg ? serializeSvg(svg) : '';
}
